import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import type { BaseDropdownProps, DropdownOption } from './BaseDropdown.types';
import { DropDownIcon } from './DropDownIcon';

export interface BaseDropdownComponentProps extends BaseDropdownProps {
  options: DropdownOption[];
  sx?: SxProps<Theme>;
  isClearable?: boolean;
}

type DropdownFieldWrapperProps = {
  id: string;
  label: string;
  error?: string;
  isDisabled?: boolean;
  required?: boolean;
  children: ReactNode;
};

const DropdownFieldWrapper = ({
  id,
  label,
  error,
  isDisabled,
  children,
  required = false,
}: DropdownFieldWrapperProps) => (
  <FormControl
    sx={{ minWidth: 120, fontFamily: 'Albert Sans' }}
    fullWidth
    error={Boolean(error)}
    disabled={isDisabled}
  >
    <InputLabel id={id}>{required ? `${label}*` : label}</InputLabel>
    {children}
    {error && <FormHelperText>{error}</FormHelperText>}
  </FormControl>
);

export const BaseDropdown = ({
  options,
  id,
  label,
  onSelection,
  selectedValue,
  error,
  isDisabled,
  required = false,
  sx,
  isClearable,
}: BaseDropdownComponentProps) => {
  const handleChange = (event: SelectChangeEvent) => {
    onSelection(event.target.value);
  };

  return (
    <DropdownFieldWrapper
      id={`${id}-label`}
      label={label}
      error={error}
      isDisabled={isDisabled}
      required={required}
    >
      <Select
        id={id}
        labelId={`${id}-label`}
        value={selectedValue?.toString() ?? ''}
        label={label}
        onChange={handleChange}
        disabled={isDisabled}
        IconComponent={(iconProps) => <DropDownIcon className={iconProps.className} />}
        key={label}
        fullWidth
        slotProps={{
          root: {
            sx: {
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            },
          },
        }}
        sx={{
          marginRight: 2,
          ...sx,
        }}
      >
        {isClearable && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} role="option">
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </DropdownFieldWrapper>
  );
};
